/**
 * Global axios response interceptor for transparent JWT access-token refresh.
 *
 * Catches 401 "Signature has expired" responses, refreshes the appropriate
 * token (root or app-scoped), and replays the original request — all
 * invisible to the calling code.
 *
 * Features:
 * - Deduplicates concurrent refresh attempts (single in-flight promise per slot)
 * - Distinguishes root vs app token by comparing the request's Authorization
 *   header against the known access-token values
 * - Skips /authorize/ endpoints to prevent infinite recursion
 * - Falls through on unrecoverable errors so handleAxiosError can redirect to login
 */

import axios, {type AxiosError, type InternalAxiosRequestConfig} from 'axios'
import {useApiCredentials} from '@/composables/useApiCredentials'
import type {IFreshToken} from '@/stores/AuthorizationStore/interfaces/IToken'

/** Extended config type carrying the retry flag. */
interface RetryableConfig extends InternalAxiosRequestConfig {
    _retry?: boolean
}

/**
 * Installs a global axios response interceptor that transparently refreshes
 * expired JWT access tokens and retries the original request.
 *
 * Call once during app bootstrap, after ``axios.defaults`` are configured.
 */
export function installTokenRefreshInterceptor(): void {
    const {
        bearerRootToken,
        bearerToken,
        rootGuid,
        guid,
        setBearerToken,
        setBearerRootToken,
    } = useApiCredentials()

    /** Shared promise per token slot — ensures only one refresh is in-flight. */
    let refreshingRoot: Promise<void> | null = null
    let refreshingApp: Promise<void> | null = null

    /**
     * Perform the actual POST to /authorize/refresh for the given token slot.
     *
     * Args:
     *     isRoot: Whether to refresh the root token or the app-scoped token.
     *
     * Raises:
     *     Error: If credentials are missing or the refresh call itself fails.
     */
    async function doRefresh(isRoot: boolean): Promise<void> {
        const scopeGuid = isRoot ? rootGuid.value : guid.value
        const refreshToken = isRoot
            ? bearerRootToken.value?.refresh_token
            : bearerToken.value?.refresh_token

        if (!scopeGuid || !refreshToken) {
            throw new Error('Missing credentials for token refresh')
        }

        const {data} = await axios.post<IFreshToken>(
            `/${scopeGuid}/authorize/refresh`,
            {},
            {
                headers: {Authorization: `Bearer ${refreshToken}`},
                // Mark the refresh call so the interceptor skips it
                _retry: true,
            } as RetryableConfig,
        )

        if (isRoot) {
            setBearerRootToken(data)
        } else {
            setBearerToken(data)
        }
    }

    axios.interceptors.response.use(
        response => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as RetryableConfig | undefined
            if (!originalRequest) {
                return Promise.reject(error)
            }

            // Only handle 401 with the exact PyJWT expiry message
            const detail = (error.response?.data as Record<string, unknown>)?.detail
            if (
                error.response?.status !== 401 ||
                detail !== 'Signature has expired'
            ) {
                return Promise.reject(error)
            }

            // Never retry auth endpoints or already-retried requests
            const url = originalRequest.url || ''
            if (originalRequest._retry || url.includes('/authorize/')) {
                return Promise.reject(error)
            }
            originalRequest._retry = true

            // Determine which token slot the failing request used
            const authHeader = (originalRequest.headers?.Authorization as string) || ''
            const isRoot =
                bearerRootToken.value != null &&
                authHeader.includes(bearerRootToken.value.access_token)
            const isApp =
                !isRoot &&
                bearerToken.value != null &&
                authHeader.includes(bearerToken.value.access_token)

            if (!isRoot && !isApp) {
                // Unknown token — cannot refresh
                return Promise.reject(error)
            }

            try {
                if (isRoot) {
                    if (!refreshingRoot) {
                        refreshingRoot = doRefresh(true).finally(() => {
                            refreshingRoot = null
                        })
                    }
                    await refreshingRoot
                    originalRequest.headers.Authorization =
                        `Bearer ${bearerRootToken.value!.access_token}`
                } else {
                    if (!refreshingApp) {
                        refreshingApp = doRefresh(false).finally(() => {
                            refreshingApp = null
                        })
                    }
                    await refreshingApp
                    originalRequest.headers.Authorization =
                        `Bearer ${bearerToken.value!.access_token}`
                }

                return axios(originalRequest)
            } catch {
                // Refresh failed (e.g. refresh token also expired) — let the
                // original 401 propagate so handleAxiosError can redirect to login.
                return Promise.reject(error)
            }
        },
    )
}
